<template>
  <div class="booking-content">
    <!-- 중앙: 클래스 예약 -->
    <div class="panel" aria-label="클래스 예약">
      <div class="panel-head">
        <h2>클래스 예약</h2>

        <!-- 날짜 선택 -->
        <div class="week">
          <button
            v-for="(d, i) in days"
            :key="d.date"
            :class="['day', { sel: i === selectedIdx }]"
            @click="selectDay(i)"
          >
            <span class="dow">{{ d.dow }}</span>
            <span class="num">{{ d.num }}</span>
          </button>
        </div>
      </div>

      <!-- 스케줄 리스트 -->
      <div class="rows">
        <ScheduleRow
          v-for="cls in schedule"
          :key="cls.id"
          :time="cls.time"
          :type="cls.type"
          :booked="cls.booked"
          :capacity="cls.capacity"
          :status="cls.status || ''"
          :mine="cls.mine || false"
          @book="book(cls)"
        />
        <p v-if="!loading && schedule.length === 0" class="empty">
          해당 날짜의 수업이 없습니다.
        </p>
        <p v-if="loading" class="empty">불러오는 중…</p>
      </div>
    </div>

    <!-- 우측: 내 예약 -->
    <aside class="right" aria-label="내 예약">
      <div class="my-panel">
        <div class="my-panel-header">
          <h3 class="panel-title">내 예약</h3>
          <div class="tabs">
            <button
              :class="['tab', { 'tab--active': myTab === 'upcoming' }]"
              @click="changeTab('upcoming')"
            >
              예정
            </button>
            <button
              :class="['tab', { 'tab--active': myTab === 'past' }]"
              @click="changeTab('past')"
            >
              지난 예약
            </button>
          </div>
        </div>

        <div class="my-panel-content">
          <div v-if="myLoading" class="my-empty">불러오는 중…</div>
          <div v-else-if="filteredBookings.length === 0" class="my-empty">
            표시할 예약이 없습니다.
          </div>

          <div v-else v-for="b in filteredBookings" :key="b.id" class="my-item">
            <div class="my-item-content">
              <div class="time">{{ b.dateText }} {{ b.time }}</div>
              <div class="my-item-bottom">
                <div class="type" :class="typeClass(b.type)">
                  {{ b.type }}
                </div>
                <span
                  class="chip"
                  :class="[
                    getAttendanceClass(b),
                    { 'chip--clickable': myTab === 'upcoming' },
                  ]"
                  @click="myTab === 'upcoming' ? cancelBooking(b) : null"
                >
                  {{ getAttendanceText(b) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </aside>

    <!-- 예약 취소 확인 모달 -->
    <div v-if="showCancelModal" class="modal-overlay" @click="closeCancelModal">
      <div class="modal-container" @click.stop>
        <h3 class="modal-title">예약 취소 확인</h3>
        <p class="modal-message">
          {{ cancelTarget?.dateText }} {{ cancelTarget?.time }} 수업 예약을
          취소하시겠습니까?
        </p>
        <div class="modal-buttons">
          <button class="modal-btn modal-btn-cancel" @click="closeCancelModal">
            돌아가기
          </button>
          <button class="modal-btn modal-btn-confirm" @click="confirmCancel">
            예약 취소
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ScheduleRow from "@/components/ScheduleRow.vue";
import { apiJson, api } from "@/lib/api";

const DOW = ["일", "월", "화", "수", "목", "금", "토"];

export default {
  name: "ClassBookingView",
  components: { ScheduleRow },
  data() {
    return {
      // 날짜/선택
      days: [], // [{ date:'2025-10-01', dow:'수', num:'1' }, ...]
      selectedIdx: 0,
      // 데이터
      loading: false,
      schedule: [], // [{ id, time, type, booked, capacity, status?, mine? }]
      // 내 예약
      myTab: "upcoming", // 'upcoming' | 'past'
      myLoading: false,
      myBookings: [], // [{ id, dateText, time, type, attended }]
      booking: false,
      // 취소 모달
      showCancelModal: false,
      cancelTarget: null,
    };
  },
  created() {
    this.initDays(); // 주간 날짜 생성
    this.loadSchedule(); // 중앙 스케줄
    this.loadMyBookings(); // 우측 패널(기본 'upcoming')
  },
  methods: {
    // ---------- 날짜 초기화/선택 ----------
    initDays() {
      const base = new Date(); // 오늘
      const day = base.getDay(); // 0=일, 1=월, ..., 6=토

      // 오늘이 일요일이면 다음날(월요일)부터 시작
      let startDate = new Date(base);
      if (day === 0) {
        startDate.setDate(base.getDate() + 1);
      }

      // 오늘부터 일요일을 제외하고 6일 수집
      this.days = [];
      let currentDate = new Date(startDate);
      let daysAdded = 0;

      while (daysAdded < 6) {
        const currentDay = currentDate.getDay();
        // 일요일이 아니면 추가
        if (currentDay !== 0) {
          const yyyy = currentDate.getFullYear();
          const mm = String(currentDate.getMonth() + 1).padStart(2, "0");
          const dd = String(currentDate.getDate()).padStart(2, "0");
          this.days.push({
            date: `${yyyy}-${mm}-${dd}`,
            dow: DOW[currentDay],
            num: String(currentDate.getDate()),
          });
          daysAdded++;
        }
        // 다음 날로 이동
        currentDate.setDate(currentDate.getDate() + 1);
      }

      // 오늘 날짜가 범위 안이면 그 인덱스를 선택
      const todayStr = this.days.findIndex(
        (d) => d.date === this.toYmd(new Date())
      );
      this.selectedIdx = todayStr >= 0 ? todayStr : 0;
    },
    selectDay(i) {
      if (i === this.selectedIdx) return;
      this.selectedIdx = i;
      this.loadSchedule();
    },
    toYmd(d) {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    },

    // ---------- 백엔드 호출 ----------
    async loadSchedule() {
      this.loading = true;
      const date = this.days[this.selectedIdx]?.date;
      try {
        // 예시: GET /booking/schedule?date=YYYY-MM-DD
        const data = await apiJson(
          `/v1/classes?date=${encodeURIComponent(date)}`
        );
        // 기대 스키마: { classes: [{ id, time:"06:00", type:"Hybrid", booked:4, capacity:12, status?, mine? }] }
        this.schedule = Array.isArray(data.classes) ? data.classes : [];
      } catch {
        this.schedule = [];
      } finally {
        this.loading = false;
      }
    },
    async loadMyBookings() {
      this.myLoading = true;
      try {
        // 예시: GET /booking/my?status=upcoming|past
        const list = await apiJson(`/v1/me/bookings?status=${this.myTab}`);
        // 기대 스키마: [{ id, date:"2025-09-30", time:"09:00", type:"Core", attended:false }]
        this.myBookings = (list || []).map((x) => ({
          ...x,
          dateText: this.prettyDate(x.date),
        }));
      } catch {
        this.myBookings = [];
      } finally {
        this.myLoading = false;
      }
    },

    // ---------- 동작 ----------
    async book(cls) {
      if (this.booking) return;
      this.booking = true;

      try {
        await apiJson(`/v1/bookings`, {
          method: "POST",
          body: JSON.stringify({ class_id: cls.id }),
        });

        // 성공 후 갱신
        await Promise.all([this.loadMyBookings(), this.loadSchedule()]);
      } catch (e) {
        // 에러는 이미 handleApiError에서 처리되므로 여기서는 추가 처리하지 않음
        console.error("Booking error:", e);
      } finally {
        this.booking = false; // 플래그 해제
      }
    },
    changeTab(tab) {
      if (this.myTab === tab) return;
      this.myTab = tab;
      this.loadMyBookings();
    },
    prettyDate(ymd) {
      // "2025-09-30" → "9월 30일 (화)" 간단 변환
      try {
        const [y, m, d] = ymd.split("-").map(Number);
        const dt = new Date(y, m - 1, d);
        return `${m}월 ${d}일 (${DOW[dt.getDay()]})`;
      } catch {
        return ymd;
      }
    },
    typeClass(t) {
      return t === "Hybrid" ? "hyb" : t === "Core" ? "core" : "car";
    },

    // ---------- 로그아웃 ----------

    getAttendanceText(booking) {
      if (this.myTab === "past") {
        return booking.attended ? "출석완료" : "불참처리";
      }
      return "예약 취소";
    },

    getAttendanceClass(booking) {
      if (this.myTab === "past") {
        return booking.attended ? "chip--attended" : "chip--absent";
      }
      return "chip--cancel";
    },

    async cancelBooking(booking) {
      this.cancelTarget = booking;
      this.showCancelModal = true;
    },

    closeCancelModal() {
      this.showCancelModal = false;
      this.cancelTarget = null;
    },

    async confirmCancel() {
      if (!this.cancelTarget) return;

      try {
        await api(`/v1/bookings/${this.cancelTarget.id}`, {
          method: "DELETE",
        });

        // 성공 후 갱신
        await Promise.all([this.loadMyBookings(), this.loadSchedule()]);

        this.closeCancelModal();
      } catch (e) {
        // 에러는 이미 handleApiError에서 처리되므로 여기서는 추가 처리하지 않음
        console.error("Cancel booking error:", e);
      }
    },
  },
  computed: {
    filteredBookings() {
      // 이미 /booking/my?status=...로 필터해서 받으므로 그대로 반환
      return this.myBookings;
    },
  },
};
</script>

<style scoped>
/* 메인: 중앙(클래스 예약) + 우측(내 예약) 2열 그리드 */
.booking-content {
  display: grid;
  grid-template-columns: 1fr 360px; /* 중앙 1fr + 우측 360px */
  gap: 24px;
  align-items: start; /* 두 칼럼 위 정렬 */
  min-width: 0; /* 자식(그리드/텍스트) 폭 확장 시 줄바꿈 방지 */
}

/* 우측 칼럼 래퍼 (원하면 sticky로 고정 가능) */
.right {
  width: 360px;
  min-width: 360px;
  align-self: start;
  /* 스크롤 고정 원하시면 아래 주석 해제
  position: sticky;
  top: 0;
  */
}

/* 중앙 패널 */
.panel {
  background: #151718;
  border-radius: 10px;
  padding: 16px;
  min-width: 0; /* 내부 콘텐츠가 길어져도 우측 칼럼 밀리지 않게 */
}

.panel-head {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  margin-bottom: 12px;
}

.panel-head h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 400;
}

/* 주간 날짜 버튼 */
.week {
  display: grid;
  grid-template-columns: repeat(6, minmax(120px, 1fr));
  gap: 12px;
  width: 100%;
  overflow-x: auto;
}

.day {
  width: 100%;
  height: 64px;
  background: #1b1d1e;
  border-radius: 6px;
  color: #fff;
  border: 0;
  cursor: pointer;
}

.day.sel {
  background: #d8d8d8;
  color: #1b1d1e;
}

.dow {
  display: block;
  font-size: 14px;
  opacity: 0.9;
  margin-top: 6px;
}

.num {
  display: block;
  font-size: 16px;
  font-weight: 500;
}

/* 스케줄 리스트 */
.rows {
  display: grid;
  gap: 12px;
}

.empty {
  padding: 20px 8px;
  color: #c8c8c8;
}

/* 우측: 내 예약 패널 */
.right .my-panel {
  background: #151718;
  border-radius: 10px;
  padding: 0;
  height: 800px;
  display: flex;
  flex-direction: column;
}

.my-panel-header {
  padding: 16px 12px 12px;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(114, 114, 114, 0.2);
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 12px;
}

.panel-title {
  margin: 0;
  font-size: 24px;
  font-weight: 400;
  color: #fff;
}

.tabs {
  display: flex;
  gap: 8px;
  align-items: center;
}

.tab {
  height: 34px;
  width: 80px;
  padding: 0 12px;
  border-radius: 5px;
  background: #1b1d1e;
  color: #8e8e8e;
  border: 0;
  cursor: pointer;
  box-sizing: border-box;
  text-align: center;
}

.tab--active {
  background: #d8d8d8;
  color: #000;
  font-weight: 600;
}

.my-panel-content {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  scrollbar-width: thin;
  scrollbar-color: #333 transparent;
}

.my-panel-content::-webkit-scrollbar {
  width: 6px;
}

.my-panel-content::-webkit-scrollbar-track {
  background: transparent;
}

.my-panel-content::-webkit-scrollbar-thumb {
  background: #333;
  border-radius: 3px;
}

.my-panel-content::-webkit-scrollbar-thumb:hover {
  background: #444;
}

.my-empty {
  padding: 12px;
  color: #c8c8c8;
}

.my-item {
  background: #0a0a0a;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 12px;
  flex-shrink: 0;
}

.my-item:last-child {
  margin-bottom: 0;
}

.my-item-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.my-item .time {
  color: #c8c8c8;
  font-size: 14px;
}

.my-item-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.my-item .type {
  font-size: 18px;
  font-weight: 600;
}

.my-item .type.hyb {
  color: #49de80;
}
.my-item .type.core {
  color: #f59e0c;
}
.my-item .type.car {
  color: #ef4444;
}

.chip {
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 10px;
  border-radius: 5px;
  font-size: 14px;
  white-space: nowrap;
  flex-shrink: 0;
}

.chip--attended {
  border: 1px solid #333;
  color: #8e8e8e;
  background: #1b1d1e;
}

.chip--absent {
  border: 1px solid #ef4444;
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
}

.chip--cancel {
  border: 1px solid #7d2828;
  color: #ef4444;
  background: #2d1a1a;
}

.chip--clickable {
  cursor: pointer;
  transition: opacity 0.2s;
}
.chip--clickable:hover {
  opacity: 0.8;
}
.chip--clickable:active {
  opacity: 0.6;
}

/* 모달 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-container {
  box-sizing: border-box;
  width: 510px;
  height: 198px;
  background: #1b1d1e;
  border: 1px solid #3a3c3d;
  border-radius: 10px;
  padding: 25px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.modal-title {
  margin: 0;
  font-family: "Pretendard", sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 20px;
  line-height: 140%;
  letter-spacing: -0.02em;
  color: #ffffff;
}

.modal-message {
  margin: 0;
  font-family: "Pretendard", sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 110%;
  letter-spacing: -0.02em;
  color: #8e8e8e;
  flex: 1;
}

.modal-buttons {
  display: flex;
  gap: 20px;
  justify-content: flex-end;
}

.modal-btn {
  width: 84px;
  height: 36px;
  border-radius: 5px;
  font-family: "Pretendard", sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 110%;
  letter-spacing: -0.02em;
  color: #ffffff;
  border: 0;
  cursor: pointer;
  transition: opacity 0.2s;
}

.modal-btn:hover {
  opacity: 0.8;
}
.modal-btn-cancel {
  box-sizing: border-box;
  background: #1b1d1e;
  border: 1px solid #333333;
}
.modal-btn-confirm {
  background: #ff4647;
}

/* 반응형: 1100px 이하에서는 1열 스택 */
@media (max-width: 1100px) {
  .booking-content {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  .right {
    width: auto;
    min-width: 0;
  }
}

/* 더 작은 화면: 날짜 선택을 가로 스크롤로 전환 */
@media (max-width: 720px) {
  .panel-head h2 {
    font-size: 20px;
  }

  .week {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding-bottom: 6px;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
  }
  .day {
    flex: 0 0 72px;
    scroll-snap-align: start;
  }
}
</style>
